/**
 * The Provider class represent a context provided by an element and allows the
 * descendent elements to consume it's value.
 *
 * The Provider provides a context for child elements through the `provides` method.
 * Changes to the Provider context value can be updated in child elements via
 * the `update` method.
 *
 * In a Stencil component, create an instance of the Provider class and define
 * the context to be provided in the `componentWillLoad` method. Make sure to
 * disconnect in the `disconnectedCallback` method.
 *
 * Example:
 * ```tsx
 * @Element() el;
 *
 * componentWillLoad() {
 *   this.provider = new Provider(this.el);
 *   this.provider.provides("context key", "value");
 * }
 *
 * disconnectedCallback() {
 *   this.provider.disconnect();
 * }
 * ```
 */
export class Provider {
    /**
     * Creates an instance of Provider.
     * @param element - The element that acts as a context provider for child elements.
     */
    constructor(element) {
        this.element = element;
        if (!this.element.__provides) {
            this.element.__provides = new Map();
        }
        this.element.setAttribute('data-sy-provider', '');
        // Add a function that gets the current value of a context. It is useful for
        // consumers that want to get the current value without subscribing to it.
        this.element.getCurrentValueOf = (contextKey) => {
            if (!this.element.__provides)
                return;
            const provider = this.element.__provides.get(contextKey);
            if (!provider)
                return;
            return provider.currentValue;
        };
        // Add a function that verifies if the element is the provider of a context.
        // It is useful for consumers when searching for the correct provider.
        this.element.isProviderOf = (contextKey) => {
            var _a;
            return !!((_a = this.element.__provides) === null || _a === void 0 ? void 0 : _a.get(contextKey));
        };
        // Function used by consumers to subscribe to value changes using a
        // callback function.
        this.element.subscribe = (contextKey, callback) => {
            if (!this.element.__provides)
                return;
            const provider = this.element.__provides.get(contextKey);
            if (!provider)
                return;
            provider.callbacks.add(callback);
            return callback(provider.currentValue);
        };
        // Function used by consumers to unsubscribe from value changes.
        this.element.unsubscribe = (contextKey, callback) => {
            if (!this.element.__provides)
                return;
            const provider = this.element.__provides.get(contextKey);
            if (!provider)
                return;
            provider.callbacks.delete(callback);
        };
    }
    /**
     * Creates a new context in the provider element for the child elements. It is
     * recommended to use this method in the `connectedCallback` method in
     * a Stencil component.
     * @param contextKey - The context key.
     * @param initialValue - The value to be provided by the context.
     */
    provides(contextKey, initialValue) {
        if (!this.element.__provides) {
            return;
        }
        if (this.element.__provides.has(contextKey)) {
            console.warn(`Failed to provide "${contextKey}". The element already has a provider 
        with a context with the "${contextKey}" key registered. Use "update" method
        to change the context value.`);
            return;
        }
        this.element.__provides.set(contextKey, {
            currentValue: initialValue,
            callbacks: new Set(),
        });
    }
    /**
     * Updates the value of a context provided by the element to the child elements
     * that consume it.
     * @param contextKey - The context key to be updated.
     * @param value - The new value of the context to be defined.
     */
    update(contextKey, value) {
        var _a;
        if (!this.element.__provides) {
            return;
        }
        const currentProvider = this.element.__provides.get(contextKey);
        if (!currentProvider) {
            console.warn(`Error when using the "update" method to change the context value in 
        the Provider: "${contextKey}". Use the "provides" method to set 
        the context before updating.`);
            return;
        }
        // Updates only if value changed.
        if (!Object.is(value, currentProvider.currentValue)) {
            currentProvider.currentValue = value;
            (_a = currentProvider.callbacks) === null || _a === void 0 ? void 0 : _a.forEach((callback) => {
                callback(value);
            });
        }
    }
    /**
     * Removes the contexts that were provided by the element for its child elements.
     */
    disconnect() {
        if (this.element.isConnected) {
            return;
        }
        this.element.removeAttribute('data-sy-provider');
        delete this.element.subscribe;
        delete this.element.unsubscribe;
        delete this.element.getCurrentValueOf;
        delete this.element.isProviderOf;
        delete this.element.__provides;
    }
}
/**
 * The Consumer class is used to consume contexts provided by a Provider element.
 * It allows the element to access the values provided via context and automatically
 * update itself when the context changes.
 *
 * To subscribe to a Consumer for a new context, use the `subscribe` method,
 * providing the context key and a callback function for assigning a value.
 *
 * You can also use the `get` method to get the current value of a context without
 * subscribing to it.
 *
 * To disconnect, use the `unsubscribe` method for a specific context or `disconnect`
 * to disconnect all Consumer contexts.
 *
 * In a Stencil component, you can create a consumer instance in the `componentWillLoad`
 * method and disconnect in the `disconnectedCallback` method.
 *
 * Example:
 * ```tsx
 * componentWillLoad() {
 *   this.consumer = new Consumer(this.el);
 *   this.consumer.subscribe('context key', (value) => console.log("Valor do contexto: ", value));
 * }
 *
 * disconnectedCallback() {
 *   this.consumer.disconnect();
 * }
 * ```
 */
export class Consumer {
    /**
     * Creates an instance of a Consumer class.
     * @param element - The element this Consumer is associated with.
     */
    constructor(element) {
        this.element = element;
        this.providers = new Map();
    }
    /**
     * Subscribes a new context to the Consumer to consume values provided by the Provider.
     * @param contextKey - The key to the context to be consumed.
     * @param callback - Callback function to update the value in the Consumer when the context changes.
     * @param options - Request options.
     * @returns - Returns the callback subscription.
     */
    subscribe(contextKey, callback, options) {
        var _a, _b;
        const newProvider = getParentProvider(this.element, contextKey);
        const providerWithCallback = this.providers.get(contextKey);
        // Already has a provider for the given contextKey.
        if (providerWithCallback) {
            const [provider] = providerWithCallback;
            if (newProvider === null || newProvider === void 0 ? void 0 : newProvider.isEqualNode(provider)) {
                return;
            }
            // If provider element changed, disconnects from the old one.
            (_a = provider.unsubscribe) === null || _a === void 0 ? void 0 : _a.call(provider, contextKey, callback);
        }
        if (!newProvider) {
            if (options === null || options === void 0 ? void 0 : options.suppress) {
                return;
            }
            console.warn(`No provider was found for the context key: ${contextKey}`);
            return;
        }
        this.providers.set(contextKey, [newProvider, callback]);
        return (_b = newProvider.subscribe) === null || _b === void 0 ? void 0 : _b.call(newProvider, contextKey, callback);
    }
    /**
     * Gets the value provided by the Provider.
     * @param contextKey - The key to the context to be consumed.
     * @param options - Request options.
     * @returns - Returns the Provider value.
     */
    get(contextKey, options) {
        var _a;
        const provider = getParentProvider(this.element, contextKey);
        if (!provider) {
            if (options === null || options === void 0 ? void 0 : options.suppress) {
                return null;
            }
            console.warn(`No provider was found for the context key: ${contextKey}`);
            return null;
        }
        return (_a = provider.getCurrentValueOf) === null || _a === void 0 ? void 0 : _a.call(provider, contextKey);
    }
    /**
     * @deprecated Use the `disconnect` method instead.
     * @param contextKey The context key to unsubscribe.
     */
    unsubscribe(contextKey) {
        var _a;
        const subscriptionInfo = this.providers.get(contextKey);
        if (subscriptionInfo) {
            const [provider, callback] = subscriptionInfo;
            (_a = provider.unsubscribe) === null || _a === void 0 ? void 0 : _a.call(provider, contextKey, callback);
            this.providers.delete(contextKey);
        }
    }
    /**
     * Removes the contexts that were provided by the element for its child elements.
     */
    disconnect() {
        // Child elements of floating elements execute disconnectedCallback each time the floating element is opened/closed.
        // In this case, the consumer is disconnected and connected again. This can cause the providers to be cleared when the
        // consumer is temporarily disconnected. To avoid this, we check if the element is connected before clearing the providers.
        if (!this.element.isConnected) {
            this.providers.forEach(([provider, callback], contextKey) => {
                var _a;
                (_a = provider.unsubscribe) === null || _a === void 0 ? void 0 : _a.call(provider, contextKey, callback);
            });
            this.providers.clear();
            return;
        }
        // Recalculate the providers for the consumer. Useful when the
        // consumer is moved on the DOM and the provider is not the same.
        this.providers.forEach(([_, callback], contextKey) => {
            this.subscribe(contextKey, callback);
        });
    }
}
/**
 * Get the closest provider element that provides the context key.
 * @param element - The element to start the search from.
 * @param contextKey - The context key to search for.
 * @returns The closest provider element that provides the context key or null if none is found.
 */
function getParentProvider(element, contextKey) {
    var _a;
    let currentElement = element;
    while ((currentElement = getParentElement(currentElement))) {
        const closestProviderElement = closestShadow(currentElement, '[data-sy-provider]');
        if (!closestProviderElement) {
            return null;
        }
        if ((_a = closestProviderElement.isProviderOf) === null || _a === void 0 ? void 0 : _a.call(closestProviderElement, contextKey)) {
            return closestProviderElement;
        }
        currentElement = closestProviderElement;
    }
    return null;
}
/**
 * Get the parent of the element ignoring the shadow DOM.
 *
 * A null value can be returned if the element is the documentElement.
 * @param element - The element to get the parent from.
 * @returns - The parent element or null if none is found.
 */
function getParentElement(element) {
    // Slotted elements are not part of the shadow tree and therefore the parent
    // will be naively returned as the <Host> element. Since we are simulating the
    // path of an event, it makes more sense to get the parent of the <slot> element
    // inside the shadowRoot.
    if (element.assignedSlot) {
        element = element.assignedSlot;
    }
    // The parent element is null both when the element is the documentElement and when it is
    // the top of the shadow tree. In the latter case, we should traverse to the host element.
    if (!element.parentElement && element !== document.documentElement) {
        return element.getRootNode().host;
    }
    return element.parentElement;
}
/**
 * Closest implementation that works with shadow DOM.
 * @param element - The element to start the search from.
 * @param selector - The selector to search for.
 * @returns - The closest element that matches the selector or null if none is found.
 */
function closestShadow(element, selector) {
    let currentElement = element;
    while (currentElement) {
        const closestElement = currentElement.closest(selector);
        if (closestElement) {
            return closestElement;
        }
        // When closestElement is null, it means that the closest element that matches
        // the selector was not found either in the light DOM or in the shadow DOM.
        // We can differentiate between the two by checking if the root node of the
        // current element is the documentElement. If it is, it means that the element is
        // in the light DOM.
        const rootNode = currentElement.getRootNode();
        const isLightDOM = rootNode === document.documentElement;
        if (isLightDOM) {
            return null;
        }
        currentElement = rootNode.host;
    }
    return null;
}
